/* ============================================================
   Dial Scroll Indicator — Dew variant
   Mr Patio Mister · desktop homepage only

   Architecture:
   - 9 major (labelled) + three-step sub-step + auto-density minor ticks
   - Major fracs computed live from HTML anchor elements + GSAP pin geometry
   - Dew styling: radial-gradient bead with depth-scaled size/opacity
   - Expand on hover: major ticks animate to even spacing (v2 style)
   - Click → Lenis smooth scroll to section anchor element
   - Color adapts: dark sections use sky tones, light sections use navy
   - Mobile: CSS hides entirely (no JS overhead)
   ============================================================ */

(function () {
  'use strict';

  /* ── Major config ────────────────────────
     nav.type:
       'top'       → always scroll to 0
       'pin-ratio' → pinST.start + ratio × (pinST.end - pinST.start)
                     viewport-stable: ratio is fixed, pin geometry scales with vh
       'anchor'    → DOM element position at click time (adapts to current layout)
     dark = section has dark background (rail + pips use light colours)
  ─────────────────────────────────────────── */
  var MAJORS = [
    { sectionId: 'hero',             label: 'Stay Cool',        dark: true,  frac: 0,
      nav: { type: 'top' } },
    { sectionId: 'brand_statement',  label: 'The Mist Promise', dark: false, frac: 0,
      nav: { type: 'pin-ratio', selector: '.brand-statement__pin', ratio: 0.6936 } },
    { sectionId: 'setup',            label: 'How It Mists',     dark: true,  frac: 0,
      nav: { type: 'pin-ratio', selector: '[data-three-step-pin]', ratio: 0.1728 } },
    { sectionId: 'environments',     label: 'Where It Mists',   dark: true,  frac: 0,
      nav: { type: 'pin-ratio', selector: '[data-three-step-pin]', ratio: 0.9546 } },
    { sectionId: 'product_features', label: 'What You Get',     dark: false, frac: 0,
      nav: { type: 'pin-ratio', selector: '.features-pin', ratio: 0.9920 } },
    { sectionId: 'email_capture',    label: 'Sign Up',          dark: false, frac: 0,
      nav: { type: 'anchor', anchorId: 'nav-email-capture',  offset: -135 } },
    { sectionId: 'testimonials',     label: 'They Love It',     dark: false, frac: 0,
      nav: { type: 'anchor', anchorId: 'nav-testimonials',   offset: -137 } },
    { sectionId: 'closing_cta',      label: 'Get Yours',        dark: true,  frac: 0,
      nav: { type: 'anchor', anchorId: 'nav-closing-cta',    offset:  -25 } },
    { sectionId: 'faq',              label: 'Questions?',       dark: false, frac: 0,
      nav: { type: 'anchor', anchorId: 'nav-faq',             offset: -157 } },
  ];

  /* ── Three-step sub-step minor ticks ─────────────────
     Ratios within the three-step pin (viewport-stable).
     Computed from user marks: (markPx - pinStart) / pinLen.
     Minor ticks only — not clickable.
  ─────────────────────────────────────────── */
  var THREE_STEP_MINOR_RATIOS = [0.3438, 0.5393, 0.7502];

  /* All 9 sections — drives dark/light/sky colour mode.
     mode 'dark' → navy/deep bg  → sky blue dots + white labels
     mode 'light' → white/frost bg → sapphire dots + navy labels
     mode 'sky'  → sky-blue bg (#D4E8F7) → navy dots + navy labels
                   (sapphire would wash out on sky-blue)              */
  var ALL_SECS = [
    { id: 'hero',             dark: true,  mode: 'dark'  },
    { id: 'brand_statement',  dark: false, mode: 'light' },
    { id: 'setup',            dark: true,  mode: 'dark'  },
    { id: 'environments',     dark: true,  mode: 'dark'  },
    { id: 'product_features', dark: false, mode: 'light' },
    { id: 'email_capture',    dark: false, mode: 'sky'   },
    { id: 'testimonials',     dark: false, mode: 'light' },
    { id: 'closing_cta',      dark: true,  mode: 'dark'  },
    { id: 'faq',              dark: false, mode: 'light' },
  ];

  /* ── Geometry constants ───────────────── */
  var TRACK_H     = 372;          // dot-track height (CSS: top:24px bottom:24px in 420px)
  var R           = TRACK_H / 2;
  var K           = 2;            // rotation rate — labels need K full turns to traverse the ring
  var HALF_WINDOW = 1 / (2 * K); // |delta| beyond this → angle clamped to back (hidden)
  var T_HIDE      = 0.30;        // depth threshold below which pip is hidden (back-half culling)

  /* ── DOM ─────────────────────────────── */
  var dial   = document.getElementById('dial-scroll');
  var inner  = document.getElementById('ds-inner');
  var dots   = document.getElementById('ds-dots');

  if (!dial || !inner || !dots) return;

  /* ── rAF handle ─────────────────────── */
  var __rafId = null;

  /* ── Cached total scroll height ─────── */
  var totalScroll = 0;
  function recomputeTotalScroll() {
    totalScroll = document.documentElement.scrollHeight - window.innerHeight;
  }
  recomputeTotalScroll();
  window.addEventListener('resize', recomputeTotalScroll);
  /* Also refresh when GSAP pin spacers change */

  /* ── State ───────────────────────────── */
  var scrollProg   = 0;
  var activeMajIdx = 0;
  var activeSecIdx = 0;    // index in ALL_SECS — drives colour mode
  var activeMode   = 'dark'; // 'dark' | 'light' | 'sky'
  var isDarkNow    = true;   // kept for CSS class toggle (ds-light)
  var expanded     = false;
  var retTimer     = null;
  var ALL_TICKS    = [];   // flat array of {frac, major, label?, sectionId?, majorIdx?}
  var tickEls      = [];   // parallel array of {wrap, pip, lbl}
  var SEC_FRACS    = [];   // parallel to ALL_SECS, filled at init

  /* ── Colour constants (rgb triplets for string concat) ── */
  var C_WHITE = '255,255,255';
  var C_NAVY  = '27,42,92';    // #1B2A5C — label text on light/sky bg
  var C_SAP   = '59,109,214';  // #3B6DD6 — dots on light sections
  var C_SKY   = '155,200,235'; // brighter sky — dots on dark sections

  /* ─────────────────────────────────────────────────────────
     Init: wait for premium scroll AND all pin spacers
  ──────────────────────────────────────────────────────────── */

  var __waitAttempts = 0;
  function waitAndInit() {
    if (__waitAttempts >= 60) {
      console.warn('[dial-scroll] data-pins-ready never set after 9s — aborting init');
      return;
    }
    __waitAttempts++;
    /* Two conditions must both be true before we measure:
       1. window.MrPM.scroll — premium-scroll.js IIFE has run, GSAP is available.
          NOTE: MrPM.scroll is set synchronously at IIFE end, BEFORE initScrollSections()
          fires (which is async inside heroLoadTl.onComplete). So MrPM.scroll alone does
          NOT guarantee pin spacers exist.
       2. data-pins-ready on <html> — set by premium-scroll.js immediately after
          initScrollSections() returns, once all pinned sections (three-step 600vh,
          environments 100vh, product-features 500vh) have created their GSAP pin spacers.
          Using this as the gate ensures buildTickMap() sees the full document height. */
    if (
      window.MrPM && window.MrPM.scroll && window.MrPM.scroll.ScrollTrigger &&
      document.documentElement.hasAttribute('data-pins-ready')
    ) {
      init();
    } else {
      setTimeout(waitAndInit, 150);
    }
  }

  /* Story 3.18: start polling as soon as DOM is parsed instead of waiting for
     the `load` event. `load` waits for ALL resources to finish — including the
     204 hero WebP frames the canvas preloads via Image() (~10MB total). On a
     cold CDN cache that adds ~60s before the dial even starts checking its
     real conditions. waitAndInit() polls for data-pins-ready + MrPM.scroll
     anyway, so it's safe to start at DOMContentLoaded — those gates still hold. */
  if (document.readyState !== 'loading') {
    waitAndInit();
  } else {
    document.addEventListener('DOMContentLoaded', waitAndInit);
  }

  /* ─────────────────────────────────────────────────────────
     STEP 1 — Build tick map from real DOM positions
  ──────────────────────────────────────────────────────────── */

  function init() {
    buildTickMap();
    buildDOM();
    bindEvents();
    /* Force initial mode sync so classes are set on first render,
       not only when user scrolls into a new section. Without this,
       the dial doesn't get ds-light until the first section change. */
    syncMode(true);
    __rafId = requestAnimationFrame(loop);
    /* Small delay so first frame renders before fade-in */
    setTimeout(function () { dial.classList.add('ds-ready'); }, 600);

    /* GSAP may recalc pin-spacers later (font load, image load, resize).
       Refresh event re-measures everything. */
    var ST = window.MrPM && window.MrPM.scroll && window.MrPM.scroll.ScrollTrigger;
    if (ST && ST.addEventListener) {
      ST.addEventListener('refresh', function () { requestAnimationFrame(buildTickMap); });
      ST.addEventListener('refresh', recomputeTotalScroll);
    }
    window.addEventListener('resize', function () {
      /* Debounced rebuild — positions can shift with viewport. */
      clearTimeout(window.__dsResizeT);
      window.__dsResizeT = setTimeout(function () { requestAnimationFrame(buildTickMap); }, 200);
    });
  }

  /* Robust section-top measurement:
     getBoundingClientRect() + scrollY gives document-relative top regardless
     of what CSS position tricks GSAP plays above the element. For pinned
     sections that are currently position:fixed, we use the pin-spacer parent
     (which stays in normal flow) instead. */
  /* Find a section element by key — handles both legacy full IDs and
     OS 2.0 JSON-template IDs (shopify-section-template--{id}__{key}). */
  function findSection(key) {
    return document.getElementById(key) ||
           document.querySelector('[id$="__' + key + '"]');
  }

  function getSectionTop(sectionId) {
    var el = findSection(sectionId);
    if (!el) return 0;

    var target = el;
    var parent = el.parentElement;
    if (parent && parent.classList && parent.classList.contains('pin-spacer')) {
      target = parent;
    }

    var rect = target.getBoundingClientRect();
    return rect.top + window.pageYOffset;
  }

  /* Find a pin ScrollTrigger by its trigger element's CSS selector. */
  function getPinSTByTrigger(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    var ST = window.MrPM && window.MrPM.scroll && window.MrPM.scroll.ScrollTrigger;
    if (!ST) return null;
    var all = ST.getAll();
    for (var i = 0; i < all.length; i++) {
      if (all[i].vars && all[i].vars.trigger === el && all[i].vars.pin) return all[i];
    }
    return null;
  }

  /* Resolve a nav descriptor to an absolute scroll pixel position.
     Returns null if the required DOM/GSAP reference isn't available. */
  function resolveNavTarget(nav) {
    if (nav.type === 'top') return 0;
    if (nav.type === 'pin-ratio') {
      var st = getPinSTByTrigger(nav.selector);
      if (!st) return null;
      return st.start + nav.ratio * (st.end - st.start);
    }
    if (nav.type === 'anchor') {
      var el = document.getElementById(nav.anchorId);
      if (!el) return null;
      return el.getBoundingClientRect().top + window.pageYOffset + (nav.offset || 0);
    }
    console.warn('[dial-scroll] resolveNavTarget: unknown nav.type or missing target', nav);
    return null;
  }

  function buildTickMap() {
    var totalScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (totalScroll < 1) totalScroll = 1;

    /* SEC_FRACS — used only for dark/light/sky colour mode detection. */
    SEC_FRACS = ALL_SECS.map(function (s) {
      return Math.min(1, Math.max(0, getSectionTop(s.id) / totalScroll));
    });

    /* Compute frac for each major from its nav descriptor.
       pin-ratio fracs are viewport-stable: ratio × current pin geometry.
       anchor fracs adapt to current DOM layout. */
    for (var i = 0; i < MAJORS.length; i++) {
      var px = resolveNavTarget(MAJORS[i].nav);
      if (px !== null) {
        MAJORS[i].frac = Math.min(1, Math.max(0, px / totalScroll));
      }
    }

    /* Build flat tick array. */
    ALL_TICKS = [];

    for (var ii = 0; ii < MAJORS.length; ii++) {
      ALL_TICKS.push({
        frac:      MAJORS[ii].frac,
        major:     true,
        label:     MAJORS[ii].label,
        sectionId: MAJORS[ii].sectionId,
        majorIdx:  ii,
        dark:      MAJORS[ii].dark,
      });

      if (ii < MAJORS.length - 1) {
        var a   = MAJORS[ii].frac;
        var b   = MAJORS[ii + 1].frac;
        var gap = b - a;

        var fracs = [];

        /* Three-step sub-steps: pin-ratio → global frac */
        if (MAJORS[ii].sectionId === 'setup') {
          var tsST = getPinSTByTrigger('[data-three-step-pin]');
          if (tsST) {
            var tsLen = tsST.end - tsST.start;
            THREE_STEP_MINOR_RATIOS.forEach(function (r) {
              var f = Math.min(1, Math.max(0, (tsST.start + r * tsLen) / totalScroll));
              if (f > a && f < b) fracs.push(f);
            });
          }
        }

        /* Auto-density: ~1 minor per 4% of scroll; min 2, max 10 */
        var count = Math.min(10, Math.max(2, Math.round(gap / 0.04)));
        for (var k = 1; k <= count; k++) {
          var autoFrac = a + gap * k / (count + 1);
          var tooClose = false;
          for (var mm = 0; mm < fracs.length; mm++) {
            if (Math.abs(fracs[mm] - autoFrac) < 0.02) { tooClose = true; break; }
          }
          if (!tooClose) fracs.push(autoFrac);
        }

        fracs.sort(function (x, y) { return x - y; });
        fracs.forEach(function (f) {
          ALL_TICKS.push({ frac: f, major: false });
        });
      }
    }

    /* On resize: if tick count changed, full DOM rebuild. */
    if (tickEls.length && tickEls.length !== ALL_TICKS.length) {
      dots.innerHTML = '';
      tickEls = [];
      /* Reset idempotency flag so bindEvents re-attaches to the same inner node */
      inner.__dsHoverBound = false;
      buildDOM();
      bindEvents();
    }
  }

  /* ─────────────────────────────────────────────────────────
     STEP 2 — Build DOM elements for each tick
  ──────────────────────────────────────────────────────────── */

  function buildDOM() {
    /* a11y: mark inner as a navigation landmark */
    inner.setAttribute('role', 'navigation');
    inner.setAttribute('aria-label', 'Page sections');

    ALL_TICKS.forEach(function (tick) {
      var wrap = document.createElement('div');
      wrap.className = 'ds-tick' + (tick.major ? ' major' : ' minor');

      var pip = document.createElement('div');
      pip.className = 'ds-pip';

      var lbl = null;
      if (tick.major) {
        /* Direct click listener — bypasses event delegation chain through
           pointer-events:none ancestors. Closure captures sectionId. */
        var sectionId = tick.sectionId;
        wrap.addEventListener('click', function () {
          if (expanded) navigateTo(sectionId);
        });

        /* a11y: keyboard activation for major ticks */
        wrap.setAttribute('role', 'button');
        wrap.setAttribute('tabindex', '0');
        wrap.setAttribute('aria-label', 'Navigate to ' + (tick.label || tick.sectionId));
        wrap.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            navigateTo(sectionId);
          }
        });
        wrap.addEventListener('focus', function () { if (!expanded) expand(); });

        lbl = document.createElement('span');
        lbl.className = 'ds-lbl';
        lbl.textContent = tick.label;
        wrap.appendChild(lbl);
      }

      wrap.appendChild(pip);
      dots.appendChild(wrap);
      tickEls.push({ wrap: wrap, pip: pip, lbl: lbl });
    });
  }

  /* ─────────────────────────────────────────────────────────
     STEP 3 — Events (hover expand/collapse, click-to-nav)
  ──────────────────────────────────────────────────────────── */

  /* Sync CSS classes to current activeSecIdx — called on init and on
     section change. isInit=true forces mode variable update even if
     activeSecIdx==0 (which starts as 0 and wouldn't otherwise trigger). */
  function syncMode(isInit) {
    isDarkNow  = ALL_SECS[activeSecIdx].dark;
    activeMode = ALL_SECS[activeSecIdx].mode;
    dial.classList.toggle('ds-light', activeMode === 'light');
    dial.classList.toggle('ds-sky',   activeMode === 'sky');
    /* No explicit 'dark' class — default state is dark-bg styling */
  }

  function navigateTo(sectionId) {
    var major = null;
    for (var i = 0; i < MAJORS.length; i++) {
      if (MAJORS[i].sectionId === sectionId) { major = MAJORS[i]; break; }
    }
    if (!major) return;

    var targetY = resolveNavTarget(major.nav);
    if (targetY === null) return;

    var lenis = window.MrPM && window.MrPM.scroll && window.MrPM.scroll.lenis;
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(targetY, { duration: 1.2 });
    } else {
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
  }

  function bindEvents() {
    if (inner.__dsHoverBound) return;
    inner.__dsHoverBound = true;

    inner.addEventListener('mouseenter', function () {
      clearTimeout(retTimer);
      if (!expanded) expand();
    });

    inner.addEventListener('mouseleave', function () {
      clearTimeout(retTimer);
      retTimer = setTimeout(function () {
        if (expanded) collapse();
      }, 180);
    });
  }

  /* ─────────────────────────────────────────────────────────
     STEP 4 — Expand / Collapse (v2 spring animation)
  ──────────────────────────────────────────────────────────── */

  function expand() {
    expanded = true;
    dial.classList.add('ds-expanded');
    var majorCount = MAJORS.length;

    tickEls.forEach(function (el, i) {
      var tick = ALL_TICKS[i];
      if (tick.major) {
        /* Evenly space majors across TRACK_H */
        var targetTop = (tick.majorIdx / (majorCount - 1)) * TRACK_H;
        el.wrap.classList.remove('ds-collapsing');
        el.wrap.classList.add('ds-animating');
        el.wrap.style.top = targetTop + 'px';
        el.wrap.style.opacity = '';
        el.wrap.style.pointerEvents = 'auto';
        /* Label inline pointer-events — CSS also sets this via .ds-expanded,
           but inline guarantees it even if CSS cascade order changes. */
        if (el.lbl) {
          el.lbl.style.pointerEvents = 'auto';
          el.lbl.style.cursor = 'pointer';
        }
      } else {
        /* Minor ticks disappear during expand */
        el.wrap.style.opacity = '0';
        el.wrap.style.pointerEvents = 'none';
      }
    });
  }

  function collapse() {
    expanded = false;
    dial.classList.remove('ds-expanded');

    tickEls.forEach(function (el, i) {
      var tick = ALL_TICKS[i];
      el.wrap.classList.remove('ds-animating');
      el.wrap.classList.add('ds-collapsing');
      if (!tick.major) {
        /* Minor ticks come back — rAF loop will reposition them */
        el.wrap.style.opacity = '';
        el.wrap.style.pointerEvents = '';
      } else if (el.lbl) {
        /* Restore label non-clickability when collapsed */
        el.lbl.style.pointerEvents = '';
        el.lbl.style.cursor = '';
      }
    });

    /* Remove transition class after animation completes */
    setTimeout(function () {
      tickEls.forEach(function (el) {
        el.wrap.classList.remove('ds-collapsing');
      });
    }, 350);
  }

  /* ─────────────────────────────────────────────────────────
     STEP 5 — rAF render loop
  ──────────────────────────────────────────────────────────── */

  function loop() {
    /* Use module-level totalScroll (cached, updated on resize + ST refresh) */
    scrollProg = totalScroll > 0 ? Math.min(1, window.pageYOffset / totalScroll) : 0;

    /* Determine active section from ALL_SECS (not just majors) — so
       `environments` / `brand_statement` also drive dark/light correctly. */
    var newSecIdx = 0;
    for (var i = ALL_SECS.length - 1; i >= 0; i--) {
      if (scrollProg >= SEC_FRACS[i] - 0.002) { newSecIdx = i; break; }
    }
    if (newSecIdx !== activeSecIdx) {
      activeSecIdx = newSecIdx;
      syncMode(false);
    }

    /* Determine active major (drives label prominence / active pip) */
    var newIdx = 0;
    for (var j = MAJORS.length - 1; j >= 0; j--) {
      if (scrollProg >= MAJORS[j].frac - 0.005) { newIdx = j; break; }
    }
    activeMajIdx = newIdx;

    if (!expanded) {
      renderCollapsed();
    } else {
      renderExpanded();
    }

    __rafId = requestAnimationFrame(loop);
  }

  window.addEventListener('pagehide', function () {
    if (__rafId) cancelAnimationFrame(__rafId);
    __rafId = null;
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#dial-scroll')) {
      if (__rafId) cancelAnimationFrame(__rafId);
      __rafId = null;
    }
  });

  /* ─────────────────────────────────────────────────────────
     Collapsed render — Classic Plus · Axis Glint styling
     · Solid rgb() dots (no bead gradient)
     · K=2 clamped rotation — each label visible near its section only
     · T_HIDE back-half culling — no rear/front Y collision
  ──────────────────────────────────────────────────────────── */

  function renderCollapsed() {
    /* Per-section colour selection:
       dark  → sky blue dots + white labels   (on navy/deep-dark bg)
       light → sapphire dots + navy labels    (on white/frost bg)
       sky   → navy dots + navy labels        (on sky-blue #D4E8F7 bg — sapphire washes out) */
    var dotColor  = activeMode === 'dark' ? C_SKY : activeMode === 'sky' ? C_NAVY : C_SAP;
    var textColor = activeMode === 'dark' ? C_WHITE : C_NAVY;

    tickEls.forEach(function (el, i) {
      var tick  = ALL_TICKS[i];
      var delta = tick.frac - scrollProg;

      /* Clamp delta so angle never wraps past ±π.
         Prevents a label from re-appearing at the front a second time
         as you scroll past its section. */
      var clampedDelta = Math.max(-HALF_WINDOW, Math.min(HALF_WINDOW, delta));
      var angle = clampedDelta * Math.PI * 2 * K;  // stays in [-π, π]
      var t     = (Math.cos(angle) + 1) / 2;       // 0 (back) → 1 (front)
      var y     = R + Math.sin(angle) * R;
      var isAct = tick.major && tick.majorIdx === activeMajIdx;

      el.wrap.style.top          = y + 'px';
      el.wrap.style.opacity      = '';
      el.wrap.style.pointerEvents = '';

      /* Hide back half — prevents rear dots overlapping front dots at same Y */
      if (t < T_HIDE) {
        el.pip.style.opacity = '0';
        if (el.lbl) el.lbl.style.opacity = '0';
        return;
      }

      /* tVis: 0→1 across the visible depth range [T_HIDE .. 1] */
      var tVis = (t - T_HIDE) / (1 - T_HIDE);

      if (tick.major) {
        var s = 7 + tVis * 9;   // 7→16px (scaled for 180px container)
        if (isAct) s += 2;

        el.pip.style.width      = s.toFixed(1) + 'px';
        el.pip.style.height     = s.toFixed(1) + 'px';
        el.pip.style.background = 'rgb(' + dotColor + ')';
        el.pip.style.boxShadow  = isAct
          ? '0 0 14px rgba(' + dotColor + ',0.65), 0 2px 5px rgba(' + dotColor + ',0.35)'
          : (tVis > 0.5 ? '0 2px 5px rgba(' + dotColor + ',' + (tVis * 0.3).toFixed(2) + ')' : 'none');
        el.pip.style.opacity    = '1';

        if (el.lbl) {
          var op = Math.max(0, Math.min(1, (tVis - 0.15) / 0.55));
          el.lbl.style.opacity       = op.toFixed(2);
          el.lbl.style.fontSize      = (9 + tVis * 2.5).toFixed(1) + 'px';
          el.lbl.style.color         = 'rgb(' + textColor + ')';
          el.lbl.style.fontWeight    = isAct ? '500' : '400';
          el.lbl.style.letterSpacing = isAct ? '0.18em' : '0.13em';
        }
      } else {
        /* Minor tick — larger + more opaque so long-section density reads */
        var ms = 3 + tVis * 3;   // 3→6px
        el.pip.style.width      = ms.toFixed(1) + 'px';
        el.pip.style.height     = ms.toFixed(1) + 'px';
        el.pip.style.background = 'rgb(' + dotColor + ')';
        el.pip.style.boxShadow  = 'none';
        el.pip.style.opacity    = (0.55 + tVis * 0.45).toFixed(2);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     Expanded render — seamless: same solid dots, repositioned.
     No hollow rings, no style change — just position animates.
  ──────────────────────────────────────────────────────────── */

  function renderExpanded() {
    var dotColor  = activeMode === 'dark' ? C_SKY : activeMode === 'sky' ? C_NAVY : C_SAP;
    var textColor = activeMode === 'dark' ? C_WHITE : C_NAVY;

    tickEls.forEach(function (el, i) {
      var tick = ALL_TICKS[i];
      if (!tick.major) return;

      var isAct = tick.majorIdx === activeMajIdx;
      var s     = isAct ? 14 : 11;

      el.pip.style.width      = s + 'px';
      el.pip.style.height     = s + 'px';
      el.pip.style.background = 'rgb(' + dotColor + ')';
      el.pip.style.boxShadow  = isAct
        ? '0 0 14px rgba(' + dotColor + ',0.65), 0 2px 6px rgba(' + dotColor + ',0.35)'
        : '0 2px 5px rgba(' + dotColor + ',0.28)';
      el.pip.style.opacity    = '1';

      if (el.lbl) {
        el.lbl.style.opacity       = '1';
        el.lbl.style.fontSize      = isAct ? '11px' : '10px';
        el.lbl.style.color         = 'rgb(' + textColor + ')';
        el.lbl.style.fontWeight    = isAct ? '500' : '400';
        el.lbl.style.letterSpacing = '0.13em';
      }
    });
  }

})();
